import type {
  TripChatExperienceRecentSearchPayload,
  TripChatFlightRecentSearchPayload,
  TripChatHotelDateSelection,
  TripChatHotelRecentSearchPayload,
  TripChatRecentSearch,
  TripChatSearchSurface,
} from 'tripPlanner/api/types'
import moment from 'moment'
import { maxFlightChildAge, maxFlightInfantAge } from 'constants/flight'
import { parseIndicativeSearchMonth, resolveSearchCalendarFocusDate } from './searchCalendarFocus'
import {
  normalizeExperienceRecentSearchPayload,
  normalizeFlightRecentSearchPayload,
  normalizeHotelRecentSearchPayload,
  normalizeRecentSearches,
  type ApiExperienceRecentSearchPayload,
  type ApiHotelRecentSearchPayload,
} from './tripChatRecentSearchPayload'

export { normalizeRecentSearches }

export type SearchDefaultsSource =
  | 'selected'
  | 'recent_stop'
  | 'recent_trip'
  | 'stop_indicative'
  | 'trip_indicative'
  | 'past_trip'
  | 'concrete'
  | 'indicative'
  | 'product'
  | 'today'

export interface SearchDefaultsSources {
  dates?: SearchDefaultsSource
  occupancy?: SearchDefaultsSource
  fareClass?: SearchDefaultsSource
  location?: SearchDefaultsSource
  airports?: SearchDefaultsSource
  focus?: SearchDefaultsSource
  sort?: SearchDefaultsSource
}

export interface ExperienceDateRange {
  checkIn?: string
  checkOut?: string
}

interface ResolveSearchDefaultsBaseInput {
  stopId?: number | null
  recentSearches?: Array<TripChatRecentSearch>
  indicativeSearchMonth?: string | null
  stopIndicativeTravellerParty?: Array<App.Occupants> | null
  tripIndicativeTravellerParty?: Array<App.Occupants> | null
  pastTripTravellerParty?: Array<App.Occupants> | null
  minDate?: Date
  maxDate?: Date
  now?: Date
}

export interface ResolveHotelSearchDefaultsInput extends ResolveSearchDefaultsBaseInput {
  surface: 'hotels'
  selected?: {
    dateSelection?: TripChatHotelDateSelection
    occupancies?: Array<App.Occupants>
    locationQuery?: string
    placeId?: string
    propertyId?: string
  }
  concrete?: {
    checkIn?: string
    checkOut?: string
    locationQuery?: string
    placeId?: string
    propertyId?: string
  }
  productDefaults?: {
    occupancies?: Array<App.Occupants>
  }
}

export interface ResolveHotelSearchDefaultsResult {
  surface: 'hotels'
  dateSelection: TripChatHotelDateSelection
  occupancies: Array<App.Occupants>
  locationQuery?: string
  placeId?: string
  propertyId?: string
  focusMonth: Date
  sources: SearchDefaultsSources
}

export interface ResolveExperienceSearchDefaultsInput extends ResolveSearchDefaultsBaseInput {
  surface: 'experiences'
  selected?: {
    dateRange?: ExperienceDateRange
    locationQuery?: string
    placeId?: string
    sort?: string
  }
  concrete?: {
    checkIn?: string
    checkOut?: string
    locationQuery?: string
    placeId?: string
  }
}

export interface ResolveExperienceSearchDefaultsResult {
  surface: 'experiences'
  dateRange: ExperienceDateRange
  locationQuery?: string
  placeId?: string
  sort?: string
  focusMonth: Date
  sources: SearchDefaultsSources
}

export interface ResolveFlightSearchDefaultsInput extends ResolveSearchDefaultsBaseInput {
  surface: 'flights'
  selected?: TripChatFlightRecentSearchPayload
  concrete?: TripChatFlightRecentSearchPayload
  flightSearchDefaults?: TripChatFlightRecentSearchPayload
  savedFlightParams?: TripChatFlightRecentSearchPayload
  productDefaults?: {
    occupants?: App.Occupants
  }
}

export interface ResolveFlightSearchDefaultsResult {
  surface: 'flights'
  originAirportCode?: string
  originAirportName?: string
  destinationAirportCode?: string
  destinationAirportName?: string
  departureDate?: string
  returnDate?: string
  fareType?: string
  fareClass?: string
  occupants?: App.Occupants
  focusMonth: Date
  sources: SearchDefaultsSources
}

export type ResolveSearchDefaultsInput =
  | ResolveHotelSearchDefaultsInput
  | ResolveExperienceSearchDefaultsInput
  | ResolveFlightSearchDefaultsInput

export type ResolveSearchDefaultsResult =
  | ResolveHotelSearchDefaultsResult
  | ResolveExperienceSearchDefaultsResult
  | ResolveFlightSearchDefaultsResult

function cloneOccupant(occupant: App.Occupants): App.Occupants {
  return { ...occupant }
}

/**
 * Hotel travellers UI keys child age selects off `childrenAge.length`, while
 * flight searches often only store child *counts*. Pad missing ages with -1
 * (unset) so the guests field and picker match the child count.
 */
function ensureHotelRoomChildAges(room: App.Occupants): App.Occupants {
  const childSlots = (room.children ?? 0) + (room.infants ?? 0) + (room.teenagers ?? 0)
  const childrenAge = [...(room.childrenAge ?? [])]

  if (childSlots <= 0 && childrenAge.length === 0) {
    return cloneOccupant(room)
  }

  while (childrenAge.length < childSlots) {
    childrenAge.push(-1)
  }

  return {
    ...room,
    childrenAge,
  }
}

function cloneHotelOccupanciesWithChildAgeSlots(
  occupancies: Array<App.Occupants>,
): Array<App.Occupants> {
  return occupancies.map((room) => ensureHotelRoomChildAges({
    ...room,
    childrenAge: room.childrenAge ? [...room.childrenAge] : undefined,
  }))
}

function flightOccupantsToHotelRooms(occupants: App.Occupants): Array<App.Occupants> {
  return cloneHotelOccupanciesWithChildAgeSlots([occupants])
}

function findMatchingRecentSearch(
  recentSearches: Array<TripChatRecentSearch> | undefined,
  stopId: number | null | undefined,
  surface: TripChatSearchSurface,
): TripChatRecentSearch | undefined {
  const normalizedStopId = stopId ?? null

  return recentSearches?.find((recentSearch) =>
    recentSearch.surface === surface &&
    (recentSearch.tripStopId ?? null) === normalizedStopId,
  )
}

/** Most recently searched record for a surface anywhere on the trip. */
function findLatestRecentSearch(
  recentSearches: Array<TripChatRecentSearch> | undefined,
  surface: TripChatSearchSurface,
): TripChatRecentSearch | undefined {
  if (!recentSearches?.length) return undefined

  return recentSearches
    .filter((recentSearch) => recentSearch.surface === surface)
    .reduce<TripChatRecentSearch | undefined>((latest, recentSearch) => {
      if (!latest) return recentSearch
      return recentSearch.searchedAt > latest.searchedAt ? recentSearch : latest
    }, undefined)
}

function hotelRoomsToFlightOccupants(rooms: Array<App.Occupants>): App.Occupants {
  return rooms.reduce<App.Occupants>((acc, room) => {
    const ages = room.childrenAge ?? []
    const knownAges = ages.filter(age => age >= 0)
    const adultAges = knownAges.filter(age => age > maxFlightChildAge)
    const infantAges = knownAges.filter(age => age <= maxFlightInfantAge)
    const childAges = knownAges.filter(age => age > maxFlightInfantAge && age <= maxFlightChildAge)
    const nonAdultCount = Math.max(
      ages.length,
      (room.children ?? 0) + (room.infants ?? 0) + (room.teenagers ?? 0),
    )
    const unknownCount = nonAdultCount - knownAges.length
    // Keep existing categories for travellers whose ages are still unknown.
    // In particular, an unset hotel child age (-1) must not become an infant.
    const unknownInfants = Math.min(unknownCount, Math.max(0, (room.infants ?? 0) - infantAges.length))
    const unknownTeenagers = Math.min(
      unknownCount - unknownInfants,
      Math.max(0, (room.teenagers ?? 0) - adultAges.length),
    )

    return {
      adults: acc.adults + (room.adults ?? 0) + adultAges.length,
      children: (acc.children ?? 0) + childAges.length + unknownCount - unknownInfants - unknownTeenagers,
      infants: (acc.infants ?? 0) + infantAges.length + unknownInfants,
      teenagers: (acc.teenagers ?? 0) + unknownTeenagers,
      // Ages belonging to promoted adults must not remain in the flight child list.
      childrenAge: [
        ...(acc.childrenAge ?? []),
        ...ages.filter(age => age <= maxFlightChildAge),
        ...Array<number>(nonAdultCount - ages.length).fill(-1),
      ],
    }
  }, { adults: 0 })
}

function getHotelRecentPayload(
  recentSearch: TripChatRecentSearch | undefined,
): TripChatHotelRecentSearchPayload | undefined {
  if (recentSearch?.surface !== 'hotels') return undefined

  return normalizeHotelRecentSearchPayload(
    recentSearch.payload as TripChatHotelRecentSearchPayload | ApiHotelRecentSearchPayload,
  )
}

function getExperienceRecentPayload(
  recentSearch: TripChatRecentSearch | undefined,
): TripChatExperienceRecentSearchPayload | undefined {
  if (recentSearch?.surface !== 'experiences') return undefined

  return normalizeExperienceRecentSearchPayload(
    recentSearch.payload as TripChatExperienceRecentSearchPayload | ApiExperienceRecentSearchPayload,
  )
}

function getFlightRecentPayload(
  recentSearch: TripChatRecentSearch | undefined,
): TripChatFlightRecentSearchPayload | undefined {
  return recentSearch?.surface === 'flights'
    ? normalizeFlightRecentSearchPayload(recentSearch.payload as TripChatFlightRecentSearchPayload)
    : undefined
}

function concreteToHotelDateSelection(
  checkIn?: string,
  checkOut?: string,
): TripChatHotelDateSelection {
  return checkIn && checkOut
    ? { type: 'specific', checkIn, checkOut }
    : { type: 'anytime' }
}

/**
 * Drop stale recent hotel dates (past / beyond horizon) so we fall through to
 * concrete stop dates — matches experiences (`validateExtractedCheckInOrCheckOutDate`)
 * and flights (`getValidFlightSearchDate`).
 */
function getValidHotelRecentDateSelection(
  selection: TripChatHotelDateSelection | undefined,
  now: Date = new Date(),
): TripChatHotelDateSelection | undefined {
  if (!selection) return undefined

  if (selection.type === 'anytime') return selection

  const today = moment(now).startOf('day')
  const maxDate = moment(now).add(3, 'years').endOf('day')

  if (selection.type === 'specific') {
    const checkIn = moment(selection.checkIn, 'YYYY-MM-DD', true).startOf('day')
    const checkOut = moment(selection.checkOut, 'YYYY-MM-DD', true).startOf('day')
    if (!checkIn.isValid() || !checkOut.isValid()) return undefined
    if (!checkIn.isAfter(today) || !checkIn.isSameOrBefore(maxDate)) return undefined
    if (!checkOut.isAfter(today) || checkOut.isBefore(checkIn, 'day')) return undefined
    return selection
  }

  if (selection.type === 'flexible' && selection.flexibleMonths) {
    const firstMonthKey = selection.flexibleMonths.split(',')[0]?.trim()
    const monthStart = parseIndicativeSearchMonth(firstMonthKey)
    if (!monthStart) return undefined
    if (moment(monthStart).isBefore(moment(now).startOf('month'))) return undefined
    return selection
  }

  return undefined
}

function getHotelDateSelectionFocusStart(
  selection: TripChatHotelDateSelection,
): string | undefined {
  if (selection.type === 'specific') {
    return selection.checkIn
  }

  if (selection.type === 'flexible' && selection.flexibleMonths) {
    // flexibleMonths is comma-separated; focus on the first month of the range.
    const firstMonthKey = selection.flexibleMonths.split(',')[0]?.trim()
    const monthStart = parseIndicativeSearchMonth(firstMonthKey)
    if (monthStart) {
      const year = monthStart.getFullYear()
      const month = String(monthStart.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}-01`
    }
  }

  return undefined
}

function getExperienceDateRangeFocusStart(dateRange: ExperienceDateRange): string | undefined {
  return dateRange.checkIn
}

function resolveFocusMonth({
  selectedStart,
  recentStart,
  concreteStart,
  indicativeSearchMonth,
  minDate,
  maxDate,
  now,
}: {
  selectedStart?: string
  recentStart?: string
  concreteStart?: string
  indicativeSearchMonth?: string | null
  minDate?: Date
  maxDate?: Date
  now?: Date
}): { focusMonth: Date; source: SearchDefaultsSource } {
  let focusStart: string | undefined
  let source: SearchDefaultsSource

  if (selectedStart) {
    focusStart = selectedStart
    source = 'selected'
  } else if (recentStart) {
    focusStart = recentStart
    source = 'recent_stop'
  } else if (concreteStart) {
    focusStart = concreteStart
    source = 'concrete'
  } else if (parseIndicativeSearchMonth(indicativeSearchMonth)) {
    source = 'indicative'
  } else {
    source = 'today'
  }

  const focusMonth = resolveSearchCalendarFocusDate({
    selectedStart: focusStart,
    indicativeSearchMonth,
    minDate,
    maxDate,
    now,
  })

  if (!focusStart) {
    source = parseIndicativeSearchMonth(indicativeSearchMonth) ? 'indicative' : 'today'
  }

  return { focusMonth, source }
}

function resolveHotelOccupancies(
  selected: Array<App.Occupants> | undefined,
  sameStopRecent: Array<App.Occupants> | undefined,
  stopIndicative: Array<App.Occupants> | null | undefined,
  tripIndicative: Array<App.Occupants> | null | undefined,
  tripHotelsRecent: Array<App.Occupants> | undefined,
  tripFlightsRecent: App.Occupants | undefined,
  pastTrip: Array<App.Occupants> | null | undefined,
  productDefault: Array<App.Occupants> | undefined,
): { occupancies: Array<App.Occupants>; source: SearchDefaultsSource } {
  if (selected?.length) {
    return { occupancies: cloneHotelOccupanciesWithChildAgeSlots(selected), source: 'selected' }
  }

  if (sameStopRecent?.length) {
    return { occupancies: cloneHotelOccupanciesWithChildAgeSlots(sameStopRecent), source: 'recent_stop' }
  }

  if (stopIndicative?.length) {
    return { occupancies: cloneHotelOccupanciesWithChildAgeSlots(stopIndicative), source: 'stop_indicative' }
  }

  if (tripIndicative?.length) {
    return { occupancies: cloneHotelOccupanciesWithChildAgeSlots(tripIndicative), source: 'trip_indicative' }
  }

  if (tripHotelsRecent?.length) {
    return { occupancies: cloneHotelOccupanciesWithChildAgeSlots(tripHotelsRecent), source: 'recent_trip' }
  }

  if (tripFlightsRecent) {
    return {
      occupancies: flightOccupantsToHotelRooms(tripFlightsRecent),
      source: 'recent_trip',
    }
  }

  if (pastTrip?.length) {
    return { occupancies: cloneHotelOccupanciesWithChildAgeSlots(pastTrip), source: 'past_trip' }
  }

  const fallback = productDefault?.length ? productDefault : [{ adults: 2 }]
  return { occupancies: cloneHotelOccupanciesWithChildAgeSlots(fallback), source: 'product' }
}

function resolveLocationFields(
  selected?: { locationQuery?: string; placeId?: string; propertyId?: string },
  recent?: { locationQuery?: string; placeId?: string; propertyId?: string },
  concrete?: { locationQuery?: string; placeId?: string; propertyId?: string },
): { locationQuery?: string; placeId?: string; propertyId?: string; source?: SearchDefaultsSource } {
  if (selected?.locationQuery !== undefined || selected?.placeId !== undefined || selected?.propertyId !== undefined) {
    return {
      locationQuery: selected.locationQuery,
      placeId: selected.placeId,
      propertyId: selected.propertyId,
      source: 'selected',
    }
  }

  if (recent?.locationQuery !== undefined || recent?.placeId !== undefined || recent?.propertyId !== undefined) {
    return {
      locationQuery: recent.locationQuery,
      placeId: recent.placeId,
      propertyId: recent.propertyId,
      source: 'recent_stop',
    }
  }

  if (concrete?.locationQuery !== undefined || concrete?.placeId !== undefined || concrete?.propertyId !== undefined) {
    return {
      locationQuery: concrete.locationQuery,
      placeId: concrete.placeId,
      propertyId: concrete.propertyId,
      source: 'concrete',
    }
  }

  return {}
}

function resolveHotelSearchDefaults(
  input: ResolveHotelSearchDefaultsInput,
): ResolveHotelSearchDefaultsResult {
  const recentSearch = findMatchingRecentSearch(input.recentSearches, input.stopId, 'hotels')
  const recentPayload = getHotelRecentPayload(recentSearch)

  let dateSelection: TripChatHotelDateSelection
  let datesSource: SearchDefaultsSource

  if (input.selected?.dateSelection !== undefined) {
    dateSelection = input.selected.dateSelection
    datesSource = 'selected'
  } else {
    const validRecentDates = getValidHotelRecentDateSelection(
      recentPayload?.dateSelection,
      input.now,
    )
    const concreteDates = concreteToHotelDateSelection(input.concrete?.checkIn, input.concrete?.checkOut)
    // A saved "anytime" search does not override a stop's known stay dates.
    // Explicit selections above still win, including choosing "anytime" now.
    const preferStopDates = input.stopId != null && validRecentDates?.type === 'anytime' && concreteDates.type === 'specific'
    if (validRecentDates && !preferStopDates) {
      dateSelection = validRecentDates
      datesSource = 'recent_stop'
    } else {
      dateSelection = concreteDates
      datesSource = 'concrete'
    }
  }

  const tripHotelsPayload = getHotelRecentPayload(
    findLatestRecentSearch(input.recentSearches, 'hotels'),
  )
  const tripFlightsPayload = getFlightRecentPayload(
    findLatestRecentSearch(input.recentSearches, 'flights'),
  )

  const { occupancies, source: occupancySource } = resolveHotelOccupancies(
    input.selected?.occupancies,
    recentPayload?.occupancies,
    input.stopIndicativeTravellerParty,
    input.tripIndicativeTravellerParty,
    tripHotelsPayload?.occupancies,
    tripFlightsPayload?.occupants,
    input.pastTripTravellerParty,
    input.productDefaults?.occupancies,
  )

  const {
    locationQuery,
    placeId,
    propertyId,
    source: locationSource,
  } = resolveLocationFields(input.selected, recentPayload, input.concrete)

  const selectionFocusStart = getHotelDateSelectionFocusStart(dateSelection)

  const { focusMonth, source: focusSource } = resolveFocusMonth({
    selectedStart: datesSource === 'selected' ? selectionFocusStart : undefined,
    recentStart: datesSource === 'recent_stop' ? selectionFocusStart : undefined,
    concreteStart: datesSource === 'concrete' && dateSelection.type === 'specific'
      ? selectionFocusStart
      : undefined,
    indicativeSearchMonth: input.indicativeSearchMonth,
    minDate: input.minDate,
    maxDate: input.maxDate,
    now: input.now,
  })

  return {
    surface: 'hotels',
    dateSelection,
    occupancies,
    locationQuery,
    placeId,
    propertyId,
    focusMonth,
    sources: {
      dates: datesSource,
      occupancy: occupancySource,
      location: locationSource,
      focus: focusSource,
    },
  }
}

function resolveExperienceSearchDefaults(
  input: ResolveExperienceSearchDefaultsInput,
): ResolveExperienceSearchDefaultsResult {
  const recentSearch = findMatchingRecentSearch(input.recentSearches, input.stopId, 'experiences')
  const recentPayload = getExperienceRecentPayload(recentSearch)

  let dateRange: ExperienceDateRange
  let datesSource: SearchDefaultsSource

  if (input.selected?.dateRange !== undefined) {
    dateRange = { ...input.selected.dateRange }
    datesSource = 'selected'
  } else if (recentPayload) {
    dateRange = {
      checkIn: recentPayload.checkIn,
      checkOut: recentPayload.checkOut,
    }
    datesSource = 'recent_stop'
  } else if (input.concrete?.checkIn || input.concrete?.checkOut) {
    dateRange = {
      checkIn: input.concrete.checkIn,
      checkOut: input.concrete.checkOut,
    }
    datesSource = 'concrete'
  } else {
    dateRange = {}
    datesSource = 'concrete'
  }

  const {
    locationQuery,
    placeId,
    source: locationSource,
  } = resolveLocationFields(input.selected, recentPayload, input.concrete)

  let sort: string | undefined
  let sortSource: SearchDefaultsSource | undefined
  if (input.selected?.sort !== undefined) {
    sort = input.selected.sort
    sortSource = 'selected'
  } else if (recentPayload?.sort !== undefined) {
    sort = recentPayload.sort
    sortSource = 'recent_stop'
  }

  const selectionFocusStart = getExperienceDateRangeFocusStart(dateRange)

  const { focusMonth, source: focusSource } = resolveFocusMonth({
    selectedStart: datesSource === 'selected' ? selectionFocusStart : undefined,
    recentStart: datesSource === 'recent_stop' ? selectionFocusStart : undefined,
    concreteStart: datesSource === 'concrete' ? selectionFocusStart : undefined,
    indicativeSearchMonth: input.indicativeSearchMonth,
    minDate: input.minDate,
    maxDate: input.maxDate,
    now: input.now,
  })

  return {
    surface: 'experiences',
    dateRange,
    locationQuery,
    placeId,
    sort,
    focusMonth,
    sources: {
      dates: datesSource,
      location: locationSource,
      focus: focusSource,
      ...(sortSource ? { sort: sortSource } : {}),
    },
  }
}

function resolveFlightField<T>(
  selected: T | undefined,
  recent: T | undefined,
  ...fallbacks: Array<T | undefined>
): { value: T | undefined; source?: SearchDefaultsSource } {
  if (selected !== undefined) {
    return { value: selected, source: 'selected' }
  }

  if (recent !== undefined) {
    return { value: recent, source: 'recent_stop' }
  }

  for (const fallback of fallbacks) {
    if (fallback !== undefined) {
      return { value: fallback, source: 'concrete' }
    }
  }

  return { value: undefined }
}

function resolveFlightSearchDefaults(
  input: ResolveFlightSearchDefaultsInput,
): ResolveFlightSearchDefaultsResult {
  const recentSearch = findMatchingRecentSearch(input.recentSearches, input.stopId, 'flights')
  const recentPayload = getFlightRecentPayload(recentSearch)
  const savedParams = input.savedFlightParams
  const defaultsParams = input.flightSearchDefaults
  const concreteParams = input.concrete
  const selected = input.selected

  const originAirportCode = resolveFlightField(
    selected?.originAirportCode,
    recentPayload?.originAirportCode,
    savedParams?.originAirportCode,
    defaultsParams?.originAirportCode,
    concreteParams?.originAirportCode,
  )
  const originAirportName = resolveFlightField(
    selected?.originAirportName,
    recentPayload?.originAirportName,
    savedParams?.originAirportName,
    defaultsParams?.originAirportName,
    concreteParams?.originAirportName,
  )
  const destinationAirportCode = resolveFlightField(
    selected?.destinationAirportCode,
    recentPayload?.destinationAirportCode,
    savedParams?.destinationAirportCode,
    defaultsParams?.destinationAirportCode,
    concreteParams?.destinationAirportCode,
  )
  const destinationAirportName = resolveFlightField(
    selected?.destinationAirportName,
    recentPayload?.destinationAirportName,
    savedParams?.destinationAirportName,
    defaultsParams?.destinationAirportName,
    concreteParams?.destinationAirportName,
  )
  const departureDate = resolveFlightField(
    selected?.departureDate,
    recentPayload?.departureDate,
    savedParams?.departureDate,
    defaultsParams?.departureDate,
    concreteParams?.departureDate,
  )
  const returnDate = resolveFlightField(
    selected?.returnDate,
    recentPayload?.returnDate,
    savedParams?.returnDate,
    defaultsParams?.returnDate,
    concreteParams?.returnDate,
  )
  const fareType = resolveFlightField(
    selected?.fareType,
    recentPayload?.fareType,
    savedParams?.fareType,
    defaultsParams?.fareType,
    concreteParams?.fareType,
  )
  const tripFlightsPayload = getFlightRecentPayload(
    findLatestRecentSearch(input.recentSearches, 'flights'),
  )
  const tripHotelsPayload = getHotelRecentPayload(
    findLatestRecentSearch(input.recentSearches, 'hotels'),
  )

  const fareClass = resolveFlightField(
    selected?.fareClass,
    recentPayload?.fareClass,
    tripFlightsPayload?.fareClass,
    savedParams?.fareClass,
    defaultsParams?.fareClass,
    concreteParams?.fareClass,
  )
  const fareClassSource = selected?.fareClass !== undefined
    ? 'selected' as const
    : recentPayload?.fareClass !== undefined
      ? 'recent_stop' as const
      : tripFlightsPayload?.fareClass !== undefined
        ? 'recent_trip' as const
        : fareClass.source

  let occupants: App.Occupants | undefined
  let occupancySource: SearchDefaultsSource | undefined
  if (selected?.occupants !== undefined) {
    occupants = cloneOccupant(selected.occupants)
    occupancySource = 'selected'
  } else if (recentPayload?.occupants !== undefined) {
    occupants = cloneOccupant(recentPayload.occupants)
    occupancySource = 'recent_stop'
  } else if (input.stopIndicativeTravellerParty?.length) {
    occupants = hotelRoomsToFlightOccupants(input.stopIndicativeTravellerParty)
    occupancySource = 'stop_indicative'
  } else if (input.tripIndicativeTravellerParty?.length) {
    occupants = hotelRoomsToFlightOccupants(input.tripIndicativeTravellerParty)
    occupancySource = 'trip_indicative'
  } else if (tripFlightsPayload?.occupants !== undefined) {
    occupants = cloneOccupant(tripFlightsPayload.occupants)
    occupancySource = 'recent_trip'
  } else if (tripHotelsPayload?.occupancies?.length) {
    occupants = hotelRoomsToFlightOccupants(tripHotelsPayload.occupancies)
    occupancySource = 'recent_trip'
  } else if (savedParams?.occupants !== undefined) {
    occupants = cloneOccupant(savedParams.occupants)
    occupancySource = 'concrete'
  } else if (defaultsParams?.occupants !== undefined) {
    occupants = cloneOccupant(defaultsParams.occupants)
    occupancySource = 'concrete'
  } else if (input.pastTripTravellerParty?.length) {
    occupants = hotelRoomsToFlightOccupants(input.pastTripTravellerParty)
    occupancySource = 'past_trip'
  } else if (input.productDefaults?.occupants !== undefined) {
    occupants = cloneOccupant(input.productDefaults.occupants)
    occupancySource = 'product'
  } else {
    occupants = { adults: 2 }
    occupancySource = 'product'
  }

  const datesSource = departureDate.source ?? returnDate.source
  const airportsSource = originAirportCode.source ?? destinationAirportCode.source

  const { focusMonth, source: focusSource } = resolveFocusMonth({
    selectedStart: selected?.departureDate,
    recentStart: recentPayload?.departureDate,
    concreteStart: savedParams?.departureDate ??
      defaultsParams?.departureDate ??
      concreteParams?.departureDate,
    indicativeSearchMonth: input.indicativeSearchMonth,
    minDate: input.minDate,
    maxDate: input.maxDate,
    now: input.now,
  })

  return {
    surface: 'flights',
    originAirportCode: originAirportCode.value,
    originAirportName: originAirportName.value,
    destinationAirportCode: destinationAirportCode.value,
    destinationAirportName: destinationAirportName.value,
    departureDate: departureDate.value,
    returnDate: returnDate.value,
    fareType: fareType.value,
    fareClass: fareClass.value,
    occupants,
    focusMonth,
    sources: {
      dates: datesSource,
      airports: airportsSource,
      occupancy: occupancySource,
      ...(fareClassSource ? { fareClass: fareClassSource } : {}),
      focus: focusSource,
    },
  }
}

export function resolveSearchDefaults(
  input: ResolveSearchDefaultsInput,
): ResolveSearchDefaultsResult {
  switch (input.surface) {
    case 'hotels':
      return resolveHotelSearchDefaults(input)
    case 'experiences':
      return resolveExperienceSearchDefaults(input)
    case 'flights':
      return resolveFlightSearchDefaults(input)
    default: {
      const exhaustiveCheck: never = input
      return exhaustiveCheck
    }
  }
}
